// dev-portal/src/pages/admin/AdminCategories.jsx

import React, { useState, useEffect } from 'react';
import { api } from '../../api/client';
import { Plus, Edit2, Trash2, GripVertical } from 'lucide-react';

export default function AdminCategories() {
    const [categories, setCategories] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        description: '',
        icon: '',
        color: '#3B82F6',
        display_order: 0,
        is_active: true
    });

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const response = await api.get('/api/admin/categories');
            // Only show parent categories (no parent_id)
            const parentCategories = response.data.filter(cat => !cat.parent_id);
            setCategories(parentCategories);
        } catch (error) {
            console.error('Failed to fetch categories:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.display_order < 0) {
            alert('Display order must be a non-negative integer');
            return;
        }

        try {
            if (editingCategory) {
                await api.put(`/api/admin/categories/${editingCategory.id}`, formData);
            } else {
                await api.post('/api/admin/categories', formData);
            }
            fetchCategories();
            resetForm();
        } catch (error) {
            console.error('Failed to save category:', error);
            alert(error.response?.data?.detail || 'Failed to save category');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this category?')) {
            try {
                await api.delete(`/api/admin/categories/${id}`);
                fetchCategories();
            } catch (error) {
                console.error('Failed to delete category:', error);
                alert('Failed to delete the category');
            }
        }
    };

    const resetForm = () => {
        setShowModal(false);
        setEditingCategory(null);
        setFormData({
            name: '',
            slug: '',
            description: '',
            icon: '',
            color: '#3B82F6',
            display_order: 0,
            is_active: true
        });
    };

    const handleEdit = (category) => {
        setEditingCategory(category);
        setFormData({
            name: category.name,
            slug: category.slug,
            description: category.description || '',
            icon: category.icon || '',
            color: category.color || '#3B82F6',
            display_order: category.display_order,
            is_active: category.is_active
        });
        setShowModal(true);
    };

    const generateSlug = (name) => {
        return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    };

    return (
        <div className="p-6">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
                <h1 className="text-xl sm:text-2xl font-bold">Category Management</h1>
                <button
                    onClick={() => setShowModal(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center gap-2 text-sm"
                >
                    <Plus className="w-4 h-4" />
                    Add Category
                </button>
            </div>

            <div className="bg-white rounded-lg shadow overflow-x-auto">
                <table className="w-full min-w-[540px]">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Order</th>
                            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Category</th>
                            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Slug</th>
                            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Icon</th>
                            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Status</th>
                            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {categories.map((category) => (
                            <tr key={category.id}>
                                <td className="px-3 sm:px-6 py-3 sm:py-4">
                                    <div className="flex items-center">
                                        <GripVertical className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                                        {category.display_order}
                                    </div>
                                </td>
                                <td className="px-3 sm:px-6 py-3 sm:py-4">
                                    <div className="flex items-center">
                                        <div
                                            className="w-7 h-7 sm:w-8 sm:h-8 rounded-full mr-2 flex-shrink-0"
                                            style={{ backgroundColor: category.color }}
                                        />
                                        <div>
                                            <div className="font-medium text-sm">{category.name}</div>
                                            <div className="text-xs text-gray-500 max-w-[150px] truncate">{category.description}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-3 sm:px-6 py-3 sm:py-4 text-sm text-gray-600 whitespace-nowrap">{category.slug}</td>
                                <td className="px-3 sm:px-6 py-3 sm:py-4 text-sm text-gray-600">{category.icon}</td>
                                <td className="px-3 sm:px-6 py-3 sm:py-4">
                                    <span className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${category.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                        {category.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td className="px-3 sm:px-6 py-3 sm:py-4">
                                    <div className="flex gap-2">
                                        <button onClick={() => handleEdit(category)} className="text-blue-600 hover:text-blue-900 p-1" title="Edit"><Edit2 className="w-4 h-4" /></button>
                                        <button onClick={() => handleDelete(category.id)} className="text-red-600 hover:text-red-900 p-1" title="Delete"><Trash2 className="w-4 h-4" /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-bold mb-4">
                            {editingCategory ? 'Edit Category' : 'Add Category'}
                        </h2>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Name</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => {
                                        setFormData({
                                            ...formData,
                                            name: e.target.value,
                                            slug: generateSlug(e.target.value)
                                        });
                                    }}
                                    className="w-full px-3 py-2 border rounded"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Slug</label>
                                <input
                                    type="text"
                                    value={formData.slug}
                                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                                    className="w-full px-3 py-2 border rounded bg-gray-50"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full px-3 py-2 border rounded"
                                    rows="3"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Icon (lucide icon name)</label>
                                <input
                                    type="text"
                                    value={formData.icon}
                                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                                    className="w-full px-3 py-2 border rounded"
                                    placeholder="e.g., building, users, stethoscope"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Color</label>
                                <input
                                    type="color"
                                    value={formData.color}
                                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                    className="w-full px-3 py-2 border rounded h-10"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Display Order</label>
                                <input
                                    type="number"
                                    value={formData.display_order}
                                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                                    className="w-full px-3 py-2 border rounded"
                                />
                            </div>

                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="is_active"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                />
                                <label htmlFor="is_active" className="text-sm">Active</label>
                            </div>

                            <div className="flex gap-2 pt-4">
                                <button
                                    type="submit"
                                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                                >
                                    {editingCategory ? 'Update' : 'Create'}
                                </button>
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}